import './Checkbox.scss';
import React, { InputHTMLAttributes, ReactNode, useEffect, useId, useMemo, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { Checkbox as MantineCheckbox } from '@mantine/core';
import { useMergedRef } from '@mantine/hooks';
import DOMPurify from 'dompurify';
import clsx from 'clsx';
import { toMantineError } from '../../theme/forms';
import { useFieldA11y } from '../../hooks/useFieldA11y';
import { FieldA11yContext, type IFieldA11y } from '../../context/FieldA11yContext';
import { decodeHtmlEntities, extractTextFromHTML } from '../../lib/html';

// Omit 'size': HTML-атрибут size:number у чекбокса бессмыслен и конфликтует с Mantine size.
export interface ICheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
    /** Ссылка на input-элемент (RHF-совместима; ранее была на label). */
    ref?: React.Ref<HTMLInputElement>;
    /** Лейбл */
    label?: React.ReactNode;
    /**
     * Содержимое сразу за текстом подписи, вне `<label>`, — значок подсказки (`ToolTipTitle`): в имя флажка не входит,
     * щелчок по нему флажок не переключает. Значок получает подпись флажка как свою («Подсказка: <подпись>»).
     */
    labelAdornment?: ReactNode;
    /** Класс строки подписи — текста и `labelAdornment` (вид подписи поля формы — `rf-form-group__label`) */
    labelClassName?: string;
    /** Описание чекбокса */
    description?: React.ReactNode;
    /** Значение (для Checkbox.Group) */
    value?: string;
    /**
     * Отображение иконки галочки
     * @default true
     * @deprecated 0 реальных потребителей; Mantine всегда показывает иконку.
     */
    icon?: boolean;
    /**
     * Вертикальное выравнивание враппера
     * @default 'flex-start'
     */
    align?: 'flex-start' | 'center' | 'flex-end';
    /**
     * Частично отмечен (indeterminate)
     * @default false
     */
    halfChecked?: boolean;
    /**
     * Положение чекбокса относительно лейбла
     * @default 'left'
     */
    position?: 'left' | 'right';
    /**
     * Круглый чекбокс
     * @default false
     */
    round?: boolean;
    /**
     * 100% ширины
     * @default false
     */
    fullWidth?: boolean;
    /** Подсказка по наведению: на тексте подписи, без подписи — на самом флажке */
    title?: string;
    /** Переключение: новое состояние (`onChange` — событие поля, как у нативного `input`) */
    onCheckedChange?: (checked: boolean) => void;
    /** Невалидный статус (текст ошибки рендерит FormGroup) */
    invalid?: boolean;
    testid?: string;
}

// Слот подписи Mantine — не `<label>`: в строке подписи `<label>` только у текста, значок рядом вне его.
// InlineInput Mantine берёт `labelElement` из обёртки корня.
const LABEL_SLOT_DIV = { labelElement: 'div' } as unknown as React.ComponentPropsWithoutRef<'div'>;

/** Текст подписи для имени значка рядом с ней; разметка подписи — без тегов */
const plainText = (label: ReactNode): string | undefined => {
    try {
        const html =
            typeof label === 'string' || typeof label === 'number'
                ? String(label)
                : React.isValidElement(label)
                  ? renderToStaticMarkup(label)
                  : '';
        return decodeHtmlEntities(extractTextFromHTML(html)).replace(/\s+/g, ' ').trim() || undefined;
    } catch {
        return undefined;
    }
};

// Фасад на Mantine Checkbox. Публичный ICheckboxProps заморожен ради потребителей (8 прямых
// + 2 контрола): legacy-пропы halfChecked/position/fullWidth/align/round/icon маппятся
// внутри. Самодельная разметка (label > input + кастомная галочка) заменена на нативный Mantine
// Checkbox. Mantine-aligned переименования (indeterminate/labelPosition) — в общий codemod.
//
// SEMI-CONTROLLED: фасад без локального состояния; Mantine нативно поддерживает оба режима
// (checked=controlled, defaultChecked=uncontrolled) — потребители используют оба. ref форвардится
// на <input> (паритет RHF field.ref для фокуса при ошибке валидации). DOMPurify сохранён —
// потребители передают HTML-лейблы. title встроен в label-span (Mantine не имеет labelProps).
const Checkbox: React.FC<ICheckboxProps> = ({
    ref,
    id,
    label,
    labelAdornment,
    labelClassName,
    description,
    value,
    invalid,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    icon = true, // принимаем для BC, игнорируем (Mantine рисует иконку сам)
    align = 'flex-start',
    halfChecked = false,
    position = 'left',
    round = false,
    fullWidth = false,
    title = '',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    required, // не пробрасываем в input (текст обязательности — у FormGroup)
    testid,
    readOnly,
    disabled,
    className,
    onChange,
    onCheckedChange,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    ...props
}) => {
    // Своя подпись чекбокса важнее подписи группы; сообщение группы — описание.
    const fieldA11y = useFieldA11y(
        { 'aria-label': props['aria-label'], 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy },
        label != null && label !== '',
    );
    const inputRef = useRef<HTMLInputElement>(null);
    const mergedRef = useMergedRef(ref, inputRef);

    // Легаси-паритет semi-controlled (EUPDEV-12528): в uncontrolled-режиме потребители
    // (DisplayField, ячейки EditTable) пересчитывают defaultChecked из
    // живого значения и ждут, что чекбокс отразит программную смену (setValues из справочника).
    // React игнорирует defaultChecked после маунта, поэтому синхронизируем DOM императивно —
    // так делал до-Mantine Checkbox. При controlled (`checked`) состоянием управляет React.
    useEffect(() => {
        if (props.checked === undefined && inputRef.current) {
            inputRef.current.checked = !!props.defaultChecked;
        }
    }, [props.defaultChecked, props.checked]);

    const sanitizedLabel = typeof label === 'string' ? DOMPurify.sanitize(label, { ADD_ATTR: ['target'] }) : null;
    const sanitizedDescription =
        typeof description === 'string' ? DOMPurify.sanitize(description, { ADD_ATTR: ['target'] }) : null;

    const labelNode: React.ReactNode =
        sanitizedLabel !== null ? (
            <span title={title || undefined} dangerouslySetInnerHTML={{ __html: sanitizedLabel }} />
        ) : label != null ? (
            <span title={title || undefined}>{label}</span>
        ) : undefined;

    // Строка подписи своя, когда рядом с текстом значок или у неё вид подписи поля: `<label for>` — только у текста
    const autoId = useId();
    const inputId = id ?? autoId;
    const hasAdornment = labelAdornment != null && labelAdornment !== false;
    const ownLine = labelNode !== undefined && (hasAdornment || !!labelClassName);
    const adornmentLabel = useMemo(() => (hasAdornment ? plainText(label) : undefined), [hasAdornment, label]);
    const adornmentA11y = useMemo<IFieldA11y>(() => ({ labelText: adornmentLabel }), [adornmentLabel]);
    const labelContent = ownLine ? (
        <div className={clsx('rf-checkbox__label-line', labelClassName)}>
            <label htmlFor={inputId} className="rf-checkbox__label-text">
                {labelNode}
                {hasAdornment && ' '}
            </label>
            {hasAdornment && (
                <span className="rf-checkbox__label-adornment">
                    <FieldA11yContext value={adornmentA11y}>{labelAdornment}</FieldA11yContext>
                </span>
            )}
        </div>
    ) : (
        labelNode
    );

    const descriptionNode: React.ReactNode =
        sanitizedDescription !== null ? (
            <span dangerouslySetInnerHTML={{ __html: sanitizedDescription }} />
        ) : (
            description
        );

    return (
        <MantineCheckbox
            ref={mergedRef}
            {...props}
            id={ownLine ? inputId : id}
            wrapperProps={ownLine ? LABEL_SLOT_DIV : undefined}
            {...fieldA11y}
            aria-invalid={invalid || props['aria-invalid'] || undefined}
            value={value === undefined || value === null ? undefined : value}
            // readOnly ≠ disabled (легаси-паритет): readOnly-чекбокс выглядит обычным и лишь
            // не интерактивен (pointer-events: none у .rf-checkbox-readonly пропускает клики
            // к родителю — в multiselect Select их ловит Combobox.Option). Маппинг readOnly
            // в disabled красил опции серым и глушил клики по квадрату (браузер не диспатчит
            // события на disabled-контролах). readOnly на input также гасит React-предупреждение
            // «checked without onChange».
            disabled={disabled}
            readOnly={readOnly}
            title={labelNode === undefined ? title || undefined : undefined}
            onChange={
                onChange || onCheckedChange
                    ? (event: React.ChangeEvent<HTMLInputElement>) => {
                          onChange?.(event);
                          onCheckedChange?.(event.currentTarget.checked);
                      }
                    : undefined
            }
            {...(readOnly ? { tabIndex: -1 } : {})}
            indeterminate={halfChecked && (value === undefined || value === null)}
            label={labelContent}
            description={descriptionNode}
            error={toMantineError(invalid)}
            // Mantine labelPosition — про ЛЕЙБЛ; наш position — про ЧЕКБОКС, поэтому инвертируем.
            labelPosition={position === 'right' ? 'left' : 'right'}
            className={clsx(
                'rf-checkbox',
                `rf-checkbox--${align}`,
                readOnly && 'rf-checkbox-readonly',
                fullWidth && 'rf-checkbox--fullWidth',
                round && 'rf-checkbox--round',
                invalid && 'rf-checkbox--invalid',
                className,
            )}
            {...(testid ? { 'data-testid': `checkbox-${testid}` } : {})}
        />
    );
};

export interface ICheckboxIndicatorProps {
    /** Отмечен @default false */
    checked?: boolean;
    /** Частично отмечен @default false */
    indeterminate?: boolean;
    disabled?: boolean;
    /** Круглая отметка @default false */
    round?: boolean;
    className?: string;
    'data-testid'?: string;
}

/**
 * Отметка флажка без поля ввода — вид `Checkbox` внутри элемента, который сам переключатель: пункт меню-флажок
 * (`menuitemcheckbox`, состояние — `aria-checked` пункта), строка списка с выбором. Диктору не видна: о состоянии
 * говорит владелец.
 */
export const CheckboxIndicator: React.FC<ICheckboxIndicatorProps> = ({
    checked = false,
    indeterminate = false,
    disabled,
    round = false,
    className,
    'data-testid': testId,
}) => (
    <MantineCheckbox.Indicator
        checked={checked}
        indeterminate={indeterminate}
        disabled={disabled}
        aria-hidden
        data-testid={testId}
        className={clsx('rf-checkbox-indicator', round && 'rf-checkbox-indicator--round', className)}
    />
);

export default Checkbox;
